import { useState, useEffect, useCallback } from 'react';
import { heroSlides } from '@/mocks/homeData';

export default function HeroCarousel() {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goToSlide = useCallback((index: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrent(index);
    setTimeout(() => setIsTransitioning(false), 700);
  }, [isTransitioning]);

  const nextSlide = useCallback(() => {
    goToSlide((current + 1) % heroSlides.length);
  }, [current, goToSlide]);

  const prevSlide = useCallback(() => {
    goToSlide((current - 1 + heroSlides.length) % heroSlides.length);
  }, [current, goToSlide]);

  useEffect(() => {
    const interval = setInterval(nextSlide, 6000);
    return () => clearInterval(interval);
  }, [nextSlide]);

  const slide = heroSlides[current];

  return (
    <section className="relative w-full h-[600px] md:h-[700px] lg:h-[800px] overflow-hidden bg-background-100">
      {/* Background Images */}
      <div className="absolute inset-0">
        {heroSlides.map((s, i) => (
          <div
            key={s.id}
            className={`absolute inset-0 transition-opacity duration-700 ease-out ${
              i === current ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={s.bgImage}
              alt=""
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-background-950/80 via-background-950/50 to-background-950/30" />
            <div className="absolute inset-0 bg-gradient-to-t from-background-950/60 via-transparent to-background-950/20" />
          </div>
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 w-full h-full flex items-center px-4 md:px-8 lg:px-12">
        <div className="max-w-3xl">
          {/* Badge */}
          <div
            key={`badge-${current}`}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500/20 backdrop-blur-sm border border-primary-400/30 rounded-full mb-6 animate-fade-in-down"
          >
            <span className="w-2 h-2 rounded-full bg-primary-400" />
            <span className="text-sm font-medium text-primary-300">{slide.badge}</span>
          </div>

          {/* Headline */}
          <h1
            key={`headline-${current}`}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-[1.1] mb-5 animate-fade-in-up"
            style={{ fontFamily: "'Segoe UI', system-ui, sans-serif" }}
          >
            {slide.headline}
          </h1>

          {/* Subtext */}
          <p
            key={`subtext-${current}`}
            className="text-base md:text-lg text-background-200 leading-relaxed max-w-xl mb-8 animate-fade-in-up"
            style={{ animationDelay: '0.1s' }}
          >
            {slide.subtext}
          </p>

          {/* CTAs */}
          <div
            key={`ctas-${current}`}
            className="flex flex-wrap gap-3 animate-fade-in-up"
            style={{ animationDelay: '0.2s' }}
          >
            <a
              href="#solutions"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector('#solutions')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              {slide.ctaPrimary}
              <i className="ri-arrow-right-line" />
            </a>
            <a
              href="#products"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector('#products')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-background-50/10 backdrop-blur-sm text-background-50 border border-background-400/30 font-medium rounded-full hover:bg-background-50/20 transition-colors whitespace-nowrap"
            >
              {slide.ctaSecondary}
              <i className="ri-arrow-right-line" />
            </a>
          </div>
        </div>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-background-50/10 backdrop-blur-sm border border-background-400/20 text-background-50 hover:bg-background-50/20 transition-colors"
        aria-label="Previous slide"
      >
        <i className="ri-arrow-left-line text-lg" />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-background-50/10 backdrop-blur-sm border border-background-400/20 text-background-50 hover:bg-background-50/20 transition-colors"
        aria-label="Next slide"
      >
        <i className="ri-arrow-right-line text-lg" />
      </button>

      {/* Dots */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {heroSlides.map((_, i) => (
          <button
            key={i}
            onClick={() => goToSlide(i)}
            className={`h-2 rounded-full transition-all duration-300 ${
              i === current ? 'w-8 bg-primary-400' : 'w-2 bg-background-400/50 hover:bg-background-300'
            }`}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 right-8 md:right-12 z-20 hidden md:flex flex-col items-center gap-2 animate-fade-in">
        <span className="text-xs text-background-400 tracking-wider uppercase rotate-90 origin-center translate-x-4">Scroll</span>
        <div className="w-px h-8 bg-background-400/40" />
      </div>
    </section>
  );
}